import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { getPlaceSuggestions } from './services/awsConfig';
import Notification from './components/Notification';
import './styles/AutocompletePage.css';

const AutocompletePage = () => {
  const [address, setAddress] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchCount, setSearchCount] = useState(parseInt(localStorage.getItem('guestSearchCount') || '0'));
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    console.log('Component mounted - user:', user, 'loading:', loading, 'searchCount:', searchCount, 'localStorage value:', localStorage.getItem('guestSearchCount'));
  }, [user, loading]);

  const handleAddressChange = async (e) => {
    const query = e.target.value;
    setAddress(query);

    if (query.length > 2) { // Start fetching suggestions after typing 3 characters
      setIsLoading(true);
      try {
        const results = await getPlaceSuggestions(query);
        setSuggestions(results);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setNotification({
          show: true,
          message: 'Error fetching address suggestions. Please try again.',
          type: 'error'
        });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
      } finally {
        setIsLoading(false);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setAddress(suggestion);
    setSuggestions([]);
  };

  const handleUnitNumberChange = (e) => {
    setUnitNumber(e.target.value);
  };

  const resetSearchCount = () => {
    setSearchCount(0);
    localStorage.removeItem('guestSearchCount');
    console.log('Search count reset to 0');
  };

  const handleGoClick = () => {
    if (address.trim()) {
      console.log('handleGoClick - user:', user, 'loading:', loading, 'searchCount:', searchCount);
      
      // Don't proceed if auth is still loading
      if (loading) {
        console.log('Auth still loading, please wait');
        return;
      }
      
      // Allow first search without auth, require auth for subsequent searches
      if (!user && searchCount >= 1) {
        console.log('Blocking search, redirecting to login');
        setNotification({
          show: true,
          message: 'You have used your free search. Please sign up or log in to continue analyzing properties.',
          type: 'info'
        });
        setTimeout(() => {
          setNotification({ show: false, message: '', type: '' });
          navigate('/login');
        }, 3000);
        return;
      }

      let fullAddress = address.trim();
      
      if (unitNumber.trim()) {
        // Parse address to find city, state, zipcode pattern
        const addressParts = fullAddress.split(',').map(part => part.trim());
        
        if (addressParts.length >= 3) {
          // Assume format: "Street, City, State Zipcode"
          // Insert unit number after street address (before city)
          addressParts.splice(1, 0, `# ${unitNumber.trim()}`);
          fullAddress = addressParts.join(', ');
        } else if (addressParts.length === 2) {
          // Check if last part contains state and zipcode
          const lastPart = addressParts[addressParts.length - 1];
          const stateZipPattern = /^([A-Z]{2}|[A-Za-z\s]+)\s+\d{5}(-\d{4})?$/;
          
          if (stateZipPattern.test(lastPart)) {
            // Format: "Street, State Zipcode" - insert unit after street
            addressParts.splice(1, 0, `# ${unitNumber.trim()}`);
            fullAddress = addressParts.join(', ');
          } else {
            // Format: "Street, City" - insert unit after street
            addressParts.splice(1, 0, `# ${unitNumber.trim()}`);
            fullAddress = addressParts.join(', ');
          }
        } else {
          // Fallback: append unit number at the end
          fullAddress = `${fullAddress}, # ${unitNumber.trim()}`;
        }
      }
      
      // Track guest searches
      if (!user) {
        const newCount = searchCount + 1;
        console.log('Incrementing search count from', searchCount, 'to', newCount);
        setSearchCount(newCount);
        localStorage.setItem('guestSearchCount', newCount.toString());
      }
      
      console.log('Full address:', fullAddress);
      
      navigate('/form', { state: { address: fullAddress, isGuest: !user } });
    }
  };

  return (
    <div className="autocomplete-page">
      {notification.show && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
        />
      )}
      
      <div className="search-container">
        <div className="hero-content">
          <h1>Find Your Next Investment Property</h1>
          <p className="subtitle">Calculate ROI and make informed decisions</p>
          
          {/* Temporary reset button for testing */}
          <button 
            onClick={resetSearchCount}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '4px',
              marginBottom: '20px',
              cursor: 'pointer'
            }}
          >
            Reset Search Count (Debug)
          </button>
          
          <div className="search-box">
            <div className="input-wrapper">
              <i className="search-icon">🔍</i>
              <input
                type="text"
                value={address}
                onChange={handleAddressChange}
                placeholder="Enter a property address..."
                className="search-input"
              />
              <input
                type="text"
                value={unitNumber}
                onChange={handleUnitNumberChange}
                placeholder="Unit #"
                className="unit-input-inline"
              />
              <button 
                onClick={handleGoClick}
                disabled={!address.trim()}
                className="go-button"
              >
                Go
              </button>
              {isLoading && <div className="loader"></div>}
            </div>
            
            {suggestions.length > 0 && (
              <ul className="suggestions-list">
                {suggestions.map((suggestion, index) => (
                  <li 
                    key={index} 
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="suggestion-item"
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      
      <div className="features-section">
        <div className="feature">
          <div className="feature-icon">📊</div>
          <h3>Accurate ROI</h3>
          <p>Get precise return on investment calculations based on real property data</p>
        </div>
        <div className="feature">
          <div className="feature-icon">🏠</div>
          <h3>Property Analysis</h3>
          <p>Analyze potential investments with comprehensive property metrics</p>
        </div>
        <div className="feature">
          <div className="feature-icon">💰</div>
          <h3>Financial Planning</h3>
          <p>Make informed decisions with detailed financial projections</p>
        </div>
      </div>
    </div>
  );
};

export default AutocompletePage;
